import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  Label,
  Pivot,
  PivotItem,
  Dropdown,
  IDropdownOption,
  SearchBox,
  TextField,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  PrimaryButton,
  DefaultButton,
  IconButton,
  Separator,
  Checkbox
} from '@fluentui/react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { approvalMatrixApi, dokumentTypenApi } from '../services/api';
import { SyncModal } from '../components/SyncModal';
import { PreisWriteBackModal } from '../components/PreisWriteBackModal';

interface IGroupMember {
  id: string;
  displayName: string;
  mail: string;
  department?: string;
  jobTitle?: string;
}

interface IUserPermissions {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
  canManagePermissions: boolean;
  isAdmin: boolean;
  isEinkauf: boolean;
}

interface IApprovalRule {
  id: string;
  department: string;
  approvers: IGroupMember[];
}

interface IUserProfileSettings {
  personalnummer?: string;
  anforderndeKostenstelle?: string;
  durchwahl?: string;
}

export const Einstellungen: React.FC = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState<string>('profil');
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  // Profil-Einstellungen state
  const [profileSettings, setProfileSettings] = useState<IUserProfileSettings>({});
  const [loadingProfileSettings, setLoadingProfileSettings] = useState(false);
  const [savingProfileSettings, setSavingProfileSettings] = useState(false);

  // Berechtigungen state
  const [availableGroups, setAvailableGroups] = useState<IDropdownOption[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<IGroupMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<IGroupMember[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [message, setMessage] = useState<{ type: MessageBarType; text: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Freigabe-Matrix state
  const [approvalRules, setApprovalRules] = useState<IApprovalRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [newDepartment, setNewDepartment] = useState('');
  const [approverSearchTerm, setApproverSearchTerm] = useState('');
  const [approverSearchResults, setApproverSearchResults] = useState<IGroupMember[]>([]);
  const [isSearchingApprovers, setIsSearchingApprovers] = useState(false);

  // Sync state
  const [artikelSyncModalOpen, setArtikelSyncModalOpen] = useState(false);
  const [kreditorenSyncModalOpen, setKreditorenSyncModalOpen] = useState(false);
  const [mengeneinheitenSyncModalOpen, setMengeneinheitenSyncModalOpen] = useState(false);
  const [lieferstatusSyncModalOpen, setLieferstatusSyncModalOpen] = useState(false);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
  const [preisWriteBackModalOpen, setPreisWriteBackModalOpen] = useState(false);
  const [preisAbweichungen, setPreisAbweichungen] = useState<any[]>([]);
  const [preisAbweichungenLoading, setPreisAbweichungenLoading] = useState(false);
  const [autoArchivTage, setAutoArchivTage] = useState<string>('');
  const [autoArchivLoaded, setAutoArchivLoaded] = useState(false);
  const [autoArchivSaving, setAutoArchivSaving] = useState(false);
  const [autoArchivMsg, setAutoArchivMsg] = useState<{ type: MessageBarType; text: string } | null>(null);

  // E-Mail / SMTP state
  const [emailSettings, setEmailSettings] = useState({
    SmtpHost: '', SmtpPort: 587, SmtpSsl: true,
    SmtpUser: '', SmtpPassword: '',
    SenderEmail: 'it-support@boegra.com', SenderName: 'Bögra-Bestellwesen',
    EinkaufEmail: '',
  });
  const [emailSettingsLoaded, setEmailSettingsLoaded] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState<{ type: MessageBarType; text: string } | null>(null);

  // E-Mail-Presets state
  interface IEmailTemplate {
    Id: number; PresetName: string; EventType: string | null;
    Subject: string; Body: string; IsActive: boolean; UpdatedAt?: string;
  }
  const [emailTemplates, setEmailTemplates] = useState<IEmailTemplate[]>([]);
  const [emailTemplatesLoaded, setEmailTemplatesLoaded] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<IEmailTemplate | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateMessage, setTemplateMessage] = useState<{ type: MessageBarType; text: string } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const bodyTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Dokumenttypen state
  const [dokTypen, setDokTypen]         = useState<any[]>([]);
  const [dokTypenLoaded, setDokTypenLoaded] = useState(false);
  const [dokTypNeuName, setDokTypNeuName]   = useState('');
  const [dokTypNeuFarbe, setDokTypNeuFarbe] = useState('#888888');
  const [dokTypNeuReihe, setDokTypNeuReihe] = useState(100);
  const [dokTypSaving, setDokTypSaving]     = useState(false);
  const [dokTypMsg, setDokTypMsg]           = useState<{ type: MessageBarType; text: string } | null>(null);
  const [editingDokTyp, setEditingDokTyp]   = useState<any | null>(null);

  // Benachrichtigungssteuerung state
  const NOTIFICATION_EVENTS = [
    { key: 'neue_bestellung',     label: 'Neue Bestellung erstellt',              desc: 'Wenn eine neue Bestellung angelegt wird, die meine Freigabe erfordert' },
    { key: 'position_freigegeben', label: 'Position freigegeben',                 desc: 'Wenn eine meiner Positionen freigegeben wurde' },
    { key: 'position_abgelehnt',  label: 'Position abgelehnt',                    desc: 'Wenn eine meiner Positionen abgelehnt wurde' },
    { key: 'flow_komplett',       label: 'Alle Flows vollständig freigegeben',    desc: 'Wenn alle Freigaben einer meiner Bestellungen erteilt wurden' },
    { key: 'kommentar',           label: 'Neuer Kommentar',                       desc: 'Wenn jemand einen Kommentar zu einer meiner Bestellungen hinterlässt' },
    { key: 'mention',             label: '@-Erwähnung',                            desc: 'Wenn ich in einem Kommentar erwähnt werde' },
    { key: 'position_geaendert', label: 'Position geändert',                      desc: 'Wenn an einer meiner Bestellungen eine Position geändert wurde (durch Einkauf, System oder andere)' },
  ];
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({});
  const [notifPrefsLoaded, setNotifPrefsLoaded] = useState(false);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [notifMessage, setNotifMessage] = useState<{ type: MessageBarType; text: string } | null>(null);

  // Load groups and approval rules on mount
  useEffect(() => {
    loadAvailableGroups();
    loadApprovalRules();
  }, []);

  // Load user-specific data when user changes
  useEffect(() => {
    if (user?.email) {
      loadPermissions();
      loadProfileSettings();
    }
  }, [user?.email]);

  const loadProfileSettings = async () => {
    try {
      setLoadingProfileSettings(true);
      const response = await axios.get('/api/users/profile-settings/me', {
        params: { email: user?.email }
      });
      setProfileSettings(response.data || {});
    } catch (error) {
      console.error('Fehler beim Laden der Profil-Einstellungen:', error);
    } finally {
      setLoadingProfileSettings(false);
    }
  };

  const handleSaveProfileSettings = async () => {
    try {
      setSavingProfileSettings(true);
      setMessage(null);
      await axios.post('/api/users/profile-settings', {
        userEmail: user?.email,
        userDisplayName: user?.displayName,
        ...profileSettings
      });
      setMessage({ type: MessageBarType.success, text: 'Profil-Einstellungen erfolgreich gespeichert' });
    } catch (error) {
      console.error('Fehler beim Speichern der Profil-Einstellungen:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Speichern der Profil-Einstellungen' });
    } finally {
      setSavingProfileSettings(false);
    }
  };

  const loadPermissions = async () => {
    try {
      setIsLoadingPermissions(true);
      const response = await axios.get<IUserPermissions>('/api/permissions/me', {
        params: { email: user?.email }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Berechtigungen:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Laden der Berechtigungen' });
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const loadAvailableGroups = async () => {
    try {
      const response = await axios.get<Array<{ key: string; name: string; description: string }>>('/api/permissions/groups');
      setAvailableGroups(
        response.data.map(g => ({
          key: g.key,
          text: g.name,
          title: g.description
        }))
      );
    } catch (error) {
      console.error('Fehler beim Laden der Gruppen:', error);
    }
  };

  const loadApprovalRules = async () => {
    try {
      setIsLoadingRules(true);
      const data = await approvalMatrixApi.getAll();

      // Transformiere API-Daten in UI-Struktur (Backend liefert bereits UI-Format)
      const rules: IApprovalRule[] = data.map((entry: any) => ({
        id: entry.Id.toString(),
        department: entry.Department,
        approvers: parseApproversFromEntry(entry)
      }));

      setApprovalRules(rules);
    } catch (error) {
      console.error('Fehler beim Laden der Freigabe-Regeln:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Laden der Freigabe-Regeln' });
    } finally {
      setIsLoadingRules(false);
    }
  };

  const parseApproversFromEntry = (entry: any): IGroupMember[] => {
    try {
      // Backend liefert jetzt Approvers als Array
      if (Array.isArray(entry.Approvers)) {
        return entry.Approvers.map((a: any) => ({
          id: a.id,
          displayName: a.name,
          mail: a.email
        }));
      }
      return [];
    } catch {
      return [];
    }
  };

  const handleGroupChange = async (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (!option) return;

    setSelectedGroup(option.key as string);
    setIsLoadingMembers(true);
    setMessage(null);
    setSearchTerm('');
    setSearchResults([]);

    try {
      const response = await axios.get<IGroupMember[]>(`/api/permissions/groups/${encodeURIComponent(option.key as string)}/members`);
      setGroupMembers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Gruppenmitglieder:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Laden der Gruppenmitglieder' });
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleSearchUsers = async (newValue: string) => {
    setSearchTerm(newValue);

    if (!newValue || newValue.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    try {
      const response = await axios.get<IGroupMember[]>('/api/permissions/users/search', {
        params: { q: newValue }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Fehler bei der Benutzersuche:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler bei der Benutzersuche' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddUserToGroup = async (user: IGroupMember) => {
    if (!selectedGroup) return;

    setIsProcessing(true);
    setMessage(null);

    try {
      await axios.post(`/api/permissions/groups/${encodeURIComponent(selectedGroup)}/members`, {
        userId: user.id
      });

      // Reload group members
      const response = await axios.get<IGroupMember[]>(`/api/permissions/groups/${encodeURIComponent(selectedGroup)}/members`);
      setGroupMembers(response.data);

      setMessage({ type: MessageBarType.success, text: `${user.displayName} wurde zur Gruppe hinzugefügt` });
      setSearchTerm('');
      setSearchResults([]);
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Benutzers:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Hinzufügen des Benutzers zur Gruppe' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveUserFromGroup = async (user: IGroupMember) => {
    if (!selectedGroup) return;

    setIsProcessing(true);
    setMessage(null);

    try {
      await axios.delete(`/api/permissions/groups/${encodeURIComponent(selectedGroup)}/members/${encodeURIComponent(user.id)}`);

      // Reload group members
      const response = await axios.get<IGroupMember[]>(`/api/permissions/groups/${encodeURIComponent(selectedGroup)}/members`);
      setGroupMembers(response.data);

      setMessage({ type: MessageBarType.success, text: `${user.displayName} wurde aus der Gruppe entfernt` });
    } catch (error) {
      console.error('Fehler beim Entfernen des Benutzers:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Entfernen des Benutzers aus der Gruppe' });
    } finally {
      setIsProcessing(false);
    }
  };

  const memberColumns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true
    },
    {
      key: 'mail',
      name: 'E-Mail',
      fieldName: 'mail',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true
    },
    {
      key: 'department',
      name: 'Abteilung',
      fieldName: 'department',
      minWidth: 120,
      maxWidth: 180,
      isResizable: true
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: IGroupMember) => (
        <IconButton
          iconProps={{ iconName: 'Delete' }}
          title="Aus Gruppe entfernen"
          ariaLabel="Aus Gruppe entfernen"
          onClick={() => handleRemoveUserFromGroup(item)}
          disabled={isProcessing}
        />
      )
    }
  ];

  const searchColumns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true
    },
    {
      key: 'mail',
      name: 'E-Mail',
      fieldName: 'mail',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true
    },
    {
      key: 'department',
      name: 'Abteilung',
      fieldName: 'department',
      minWidth: 120,
      maxWidth: 180,
      isResizable: true
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: IGroupMember) => (
        <PrimaryButton
          text="Hinzufügen"
          onClick={() => handleAddUserToGroup(item)}
          disabled={isProcessing}
        />
      )
    }
  ];

  const renderBerechtigungen = () => {
    if (!permissions?.canManagePermissions) {
      return (
        <Stack tokens={{ childrenGap: 20 }}>
          <Label style={{ fontSize: 20, fontWeight: 600 }}>Berechtigungen verwalten</Label>
          <MessageBar messageBarType={MessageBarType.warning}>
            Sie haben keine Berechtigung, Benutzerberechtigungen zu verwalten.
          </MessageBar>
        </Stack>
      );
    }

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Label style={{ fontSize: 20, fontWeight: 600 }}>Berechtigungen verwalten</Label>
        <Text>
          Verwalten Sie Active Directory-Benutzer in den verschiedenen Benutzergruppen des Bestellwesens.
        </Text>

        {message && (
          <MessageBar
            messageBarType={message.type}
            onDismiss={() => setMessage(null)}
          >
            {message.text}
          </MessageBar>
        )}

        <Dropdown
          label="Gruppe auswählen"
          placeholder="Wählen Sie eine Gruppe"
          options={availableGroups}
          selectedKey={selectedGroup}
          onChange={handleGroupChange}
        />

        {selectedGroup && (
          <>
            <Separator>Gruppenmitglieder</Separator>

            {isLoadingMembers ? (
              <Spinner size={SpinnerSize.medium} label="Lade Gruppenmitglieder..." />
            ) : (
              <>
                <Text>{groupMembers.length} Mitglied(er) in dieser Gruppe</Text>
                {groupMembers.length > 0 && (
                  <DetailsList
                    items={groupMembers}
                    columns={memberColumns}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                    isHeaderVisible={true}
                  />
                )}
              </>
            )}

            <Separator>Benutzer zur Gruppe hinzufügen</Separator>

            <SearchBox
              placeholder="Benutzer suchen (Name oder E-Mail)"
              value={searchTerm}
              onChange={(_, newValue) => handleSearchUsers(newValue || '')}
              onClear={() => {
                setSearchTerm('');
                setSearchResults([]);
              }}
            />

            {isSearching && (
              <Spinner size={SpinnerSize.small} label="Suche..." />
            )}

            {searchResults.length > 0 && (
              <>
                <Text>{searchResults.length} Benutzer gefunden</Text>
                <DetailsList
                  items={searchResults}
                  columns={searchColumns}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  isHeaderVisible={true}
                />
              </>
            )}
          </>
        )}
      </Stack>
    );
  };

  const handleSearchApprovers = async (newValue: string) => {
    setApproverSearchTerm(newValue);

    if (!newValue || newValue.trim().length < 2) {
      setApproverSearchResults([]);
      return;
    }

    setIsSearchingApprovers(true);

    try {
      const response = await axios.get<IGroupMember[]>('/api/users/search', {
        params: { q: newValue }
      });
      setApproverSearchResults(response.data);
    } catch (error) {
      console.error('Fehler bei der Genehmiger-Suche:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler bei der Genehmiger-Suche' });
    } finally {
      setIsSearchingApprovers(false);
    }
  };

  const handleAddApprover = async (approver: IGroupMember) => {
    if (!editingRuleId) return;

    setIsProcessing(true);
    setMessage(null);

    try {
      const rule = approvalRules.find(r => r.id === editingRuleId);
      if (!rule) return;

      // Verhindere Duplikate
      if (rule.approvers.some(a => a.id === approver.id)) {
        setMessage({ type: MessageBarType.warning, text: 'Dieser Genehmiger ist bereits zugeordnet' });
        return;
      }

      const updatedApprovers = [...rule.approvers, approver];

      // Konvertiere zu Backend-Format — UPN bevorzugen (konsistent mit Login-E-Mail)
      const approversForBackend = updatedApprovers.map(a => ({
        id: a.id,
        name: a.displayName,
        email: (a as any).userPrincipalName || a.mail
      }));

      await approvalMatrixApi.update(parseInt(editingRuleId), approversForBackend);

      await loadApprovalRules();
      setMessage({ type: MessageBarType.success, text: `${approver.displayName} wurde hinzugefügt` });
      setApproverSearchTerm('');
      setApproverSearchResults([]);
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Genehmigers:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Hinzufügen des Genehmigers' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveApprover = async (ruleId: string, approverId: string) => {
    setIsProcessing(true);
    setMessage(null);

    try {
      const rule = approvalRules.find(r => r.id === ruleId);
      if (!rule) return;

      const updatedApprovers = rule.approvers.filter(a => a.id !== approverId);

      // Konvertiere zu Backend-Format — UPN bevorzugen (konsistent mit Login-E-Mail)
      const approversForBackend = updatedApprovers.map(a => ({
        id: a.id,
        name: a.displayName,
        email: (a as any).userPrincipalName || a.mail
      }));

      await approvalMatrixApi.update(parseInt(ruleId), approversForBackend);

      await loadApprovalRules();
      setMessage({ type: MessageBarType.success, text: 'Genehmiger wurde entfernt' });
    } catch (error) {
      console.error('Fehler beim Entfernen des Genehmigers:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Entfernen des Genehmigers' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateRule = async () => {
    if (!newDepartment.trim()) {
      setMessage({ type: MessageBarType.error, text: 'Bitte geben Sie eine Abteilung ein' });
      return;
    }

    // Prüfe ob Abteilung bereits existiert
    if (approvalRules.some(r => r.department.toLowerCase() === newDepartment.trim().toLowerCase())) {
      setMessage({ type: MessageBarType.error, text: 'Diese Abteilung existiert bereits' });
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      // Erstelle mit leerem Approvers-Array
      await approvalMatrixApi.create(newDepartment.trim(), []);

      await loadApprovalRules();
      setMessage({ type: MessageBarType.success, text: `Abteilung "${newDepartment}" wurde erstellt` });
      setNewDepartment('');
    } catch (error) {
      console.error('Fehler beim Erstellen der Abteilung:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Erstellen der Abteilung' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteRule = async (ruleId: string, department: string) => {
    if (!window.confirm(`Möchten Sie die Freigabe-Regel für "${department}" wirklich löschen?`)) {
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      await approvalMatrixApi.delete(parseInt(ruleId));
      await loadApprovalRules();
      setMessage({ type: MessageBarType.success, text: `Freigabe-Regel für "${department}" wurde gelöscht` });
      setEditingRuleId(null);
    } catch (error) {
      console.error('Fehler beim Löschen der Regel:', error);
      setMessage({ type: MessageBarType.error, text: 'Fehler beim Löschen der Freigabe-Regel' });
    } finally {
      setIsProcessing(false);
    }
  };

  const approverColumns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true
    },
    {
      key: 'mail',
      name: 'E-Mail',
      fieldName: 'mail',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 80,
      maxWidth: 80,
      onRender: (item?: IGroupMember) => {
        if (!item) return null;
        const ruleId = editingRuleId;
        return (
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Genehmiger entfernen"
            ariaLabel="Genehmiger entfernen"
            onClick={() => ruleId && handleRemoveApprover(ruleId, item.id)}
            disabled={isProcessing}
          />
        );
      }
    }
  ];

  const approverSearchColumns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true
    },
    {
      key: 'mail',
      name: 'E-Mail',
      fieldName: 'mail',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true
    },
    {
      key: 'department',
      name: 'Abteilung',
      fieldName: 'department',
      minWidth: 120,
      maxWidth: 180,
      isResizable: true
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: IGroupMember) => (
        <PrimaryButton
          text="Hinzufügen"
          onClick={() => handleAddApprover(item)}
          disabled={isProcessing}
        />
      )
    }
  ];

  const renderFreigabe = () => {
    if (!permissions?.canManagePermissions) {
      return (
        <Stack tokens={{ childrenGap: 20 }}>
          <Label style={{ fontSize: 20, fontWeight: 600 }}>Freigabe-Matrix verwalten</Label>
          <MessageBar messageBarType={MessageBarType.warning}>
            Sie haben keine Berechtigung, die Freigabe-Matrix zu verwalten.
          </MessageBar>
        </Stack>
      );
    }

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Label style={{ fontSize: 20, fontWeight: 600 }}>Freigabe-Matrix</Label>
        <Text>
          Verwalten Sie die Zuordnung von Abteilungen zu Genehmigern. Bestellungen aus einer Abteilung müssen von den zugeordneten Department- und Management-Genehmigern freigegeben werden.
        </Text>

        {message && (
          <MessageBar
            messageBarType={message.type}
            onDismiss={() => setMessage(null)}
          >
            {message.text}
          </MessageBar>
        )}

        <Separator>Neue Abteilung erstellen</Separator>

        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
          <TextField
            label="Abteilungsname"
            placeholder="z.B. EDV, Vertrieb, Einkauf"
            value={newDepartment}
            onChange={(_, value) => setNewDepartment(value || '')}
            disabled={isProcessing}
            styles={{ root: { flexGrow: 1 } }}
          />
          <PrimaryButton
            text="Erstellen"
            iconProps={{ iconName: 'Add' }}
            onClick={handleCreateRule}
            disabled={isProcessing || !newDepartment.trim()}
          />
        </Stack>

        <Separator>Bestehende Abteilungen</Separator>

        {isLoadingRules ? (
          <Spinner size={SpinnerSize.medium} label="Lade Freigabe-Regeln..." />
        ) : approvalRules.length === 0 ? (
          <MessageBar messageBarType={MessageBarType.info}>
            Noch keine Freigabe-Regeln vorhanden. Erstellen Sie eine neue Abteilung.
          </MessageBar>
        ) : (
          <Stack tokens={{ childrenGap: 16 }}>
            {approvalRules.map(rule => (
              <Stack
                key={rule.id}
                styles={{
                  root: {
                    padding: 16,
                    border: '1px solid #edebe9',
                    borderRadius: 4,
                    backgroundColor: editingRuleId === rule.id ? '#f3f2f1' : 'white'
                  }
                }}
                tokens={{ childrenGap: 12 }}
              >
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                  <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                    <Label styles={{ root: { fontSize: 16, fontWeight: 600, margin: 0 } }}>
                      {rule.department}
                    </Label>
                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                      ({rule.approvers.length} Genehmiger)
                    </Text>
                  </Stack>
                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                      text={editingRuleId === rule.id ? "Schließen" : "Bearbeiten"}
                      iconProps={{ iconName: editingRuleId === rule.id ? 'ChevronUp' : 'ChevronDown' }}
                      onClick={() => setEditingRuleId(editingRuleId === rule.id ? null : rule.id)}
                    />
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      title="Abteilung löschen"
                      onClick={() => handleDeleteRule(rule.id, rule.department)}
                      disabled={isProcessing}
                    />
                  </Stack>
                </Stack>

                {editingRuleId === rule.id && (
                  <>
                    <Separator />

                    <Stack tokens={{ childrenGap: 12 }}>
                      <Label>Zugeordnete Genehmiger</Label>
                      {rule.approvers.length === 0 ? (
                        <MessageBar messageBarType={MessageBarType.warning}>
                          Noch keine Genehmiger zugeordnet. Fügen Sie Genehmiger hinzu.
                        </MessageBar>
                      ) : (
                        <DetailsList
                          items={rule.approvers}
                          columns={approverColumns}
                          layoutMode={DetailsListLayoutMode.justified}
                          selectionMode={SelectionMode.none}
                          isHeaderVisible={true}
                        />
                      )}
                    </Stack>

                    <Separator />

                    <Stack tokens={{ childrenGap: 12 }}>
                      <Label>Genehmiger hinzufügen</Label>
                      <SearchBox
                        placeholder="Benutzer suchen (Name oder E-Mail)"
                        value={approverSearchTerm}
                        onChange={(_, newValue) => handleSearchApprovers(newValue || '')}
                        onClear={() => {
                          setApproverSearchTerm('');
                          setApproverSearchResults([]);
                        }}
                      />

                      {isSearchingApprovers && (
                        <Spinner size={SpinnerSize.small} label="Suche..." />
                      )}

                      {approverSearchResults.length > 0 && (
                        <>
                          <Text>{approverSearchResults.length} Benutzer gefunden</Text>
                          <DetailsList
                            items={approverSearchResults}
                            columns={approverSearchColumns}
                            layoutMode={DetailsListLayoutMode.justified}
                            selectionMode={SelectionMode.none}
                            isHeaderVisible={true}
                          />
                        </>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    );
  };

  const loadSyncHistory = async () => {
    setSyncHistoryLoading(true);
    try {
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
      const resp = await axios.get(`${baseUrl}/api/sync/history?limit=40`, { withCredentials: true });
      setSyncHistory(resp.data);
    } catch {
      // Tabelle noch nicht vorhanden (Migration ausstehend) — kein Fehler anzeigen
    } finally {
      setSyncHistoryLoading(false);
    }
  };

  const loadAutoArchivSettings = async () => {
    if (autoArchivLoaded) return;
    try {
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
      const resp = await axios.get(`${baseUrl}/api/sync/settings`, { withCredentials: true });
      const tage = resp.data?.AutoArchivNachLieferungTage;
      setAutoArchivTage(tage != null ? String(tage) : '');
      setAutoArchivLoaded(true);
    } catch { /* ignorieren */ }
  };

  const saveAutoArchivSettings = async () => {
    setAutoArchivSaving(true);
    setAutoArchivMsg(null);
    try {
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
      const tage = autoArchivTage.trim() === '' ? null : parseInt(autoArchivTage, 10);
      if (tage !== null && (isNaN(tage) || tage < 1)) {
        setAutoArchivMsg({ type: MessageBarType.error, text: 'Bitte eine positive Ganzzahl eingeben oder das Feld leer lassen (= deaktiviert).' });
        return;
      }
      await axios.put(`${baseUrl}/api/sync/settings`, { AutoArchivNachLieferungTage: tage }, { withCredentials: true });
      setAutoArchivMsg({ type: MessageBarType.success, text: tage ? `Auto-Archivierung aktiv: ${tage} Tage nach Wareneingang.` : 'Auto-Archivierung deaktiviert.' });
    } catch (e: any) {
      setAutoArchivMsg({ type: MessageBarType.error, text: 'Fehler: ' + (e.response?.data?.error || e.message) });
    } finally {
      setAutoArchivSaving(false);
    }
  };

  const syncTypeLabel: Record<string, string> = {
    artikel: 'Artikel',
    kreditoren: 'Kreditoren',
    mengeneinheiten: 'Mengeneinheiten',
    lagerorte: 'Lagerorte',
    lieferstatus: 'Lieferstatus',
    'auto-archivierung': 'Auto-Archivierung',
  };

  const renderSync = () => {
    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Label style={{ fontSize: 20, fontWeight: 600 }}>Business Central Synchronisation</Label>

        <Text>
          Synchronisiere Daten aus Business Central in die lokale Datenbank.
          Artikel, Kreditoren und Mengeneinheiten werden automatisch erstellt oder aktualisiert.
        </Text>

        <Separator />

        {/* Zentrierte Buttons */}
        <Stack
          horizontal
          tokens={{ childrenGap: 20 }}
          horizontalAlign="center"
          styles={{ root: { marginTop: 40, marginBottom: 40 } }}
        >
          <button
            onClick={() => setArtikelSyncModalOpen(true)}
            style={{
              padding: '16px 32px',
              fontSize: 16,
              fontWeight: 600,
              backgroundColor: '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              minWidth: 250
            }}
          >
            Artikel aktualisieren
          </button>

          <button
            onClick={() => setKreditorenSyncModalOpen(true)}
            style={{
              padding: '16px 32px',
              fontSize: 16,
              fontWeight: 600,
              backgroundColor: '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              minWidth: 250
            }}
          >
            Kreditoren aktualisieren
          </button>

          <button
            onClick={() => setMengeneinheitenSyncModalOpen(true)}
            style={{
              padding: '16px 32px',
              fontSize: 16,
              fontWeight: 600,
              backgroundColor: '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              minWidth: 250
            }}
          >
            Mengeneinheiten aktualisieren
          </button>

          <button
            onClick={() => setLieferstatusSyncModalOpen(true)}
            style={{
              padding: '16px 32px',
              fontSize: 16,
              fontWeight: 600,
              backgroundColor: '#107c10',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              minWidth: 250
            }}
          >
            Lieferstatus aktualisieren
          </button>
        </Stack>

        {/* Preis Write-Back zu BC */}
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: '16px', backgroundColor: '#fff8e1', borderRadius: 4, border: '1px solid #f4c430' } }}>
          <Text variant="medium" style={{ fontWeight: 600 }}>Preise → Business Central</Text>
          <Text style={{ color: '#605e5c' }}>
            Schreibt Artikel-Einstandspreise, die lokal neuer als in BC sind, zurück nach Business Central (via Browser-Automatisierung).
          </Text>
          <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
            <button
              onClick={async () => {
                setPreisAbweichungenLoading(true);
                try {
                  const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
                  const resp = await axios.get(`${baseUrl}/api/sync/artikel-preise-abweichungen`, { withCredentials: true });
                  const data = resp.data;
                  if (!Array.isArray(data)) {
                    throw new Error(`Unerwartete Server-Antwort: ${JSON.stringify(data).substring(0, 200)}`);
                  }
                  setPreisAbweichungen(data);
                } catch (e: any) {
                  alert('Fehler: ' + e.message);
                } finally {
                  setPreisAbweichungenLoading(false);
                }
              }}
              disabled={preisAbweichungenLoading}
              style={{ padding: '8px 16px', fontSize: 14, backgroundColor: '#f3f2f1', color: '#323130', border: '1px solid #8a8886', borderRadius: 4, cursor: 'pointer' }}
            >
              {preisAbweichungenLoading ? 'Prüfe...' : 'Abweichungen prüfen'}
            </button>
            {preisAbweichungen.length > 0 && (
              <button
                onClick={() => setPreisWriteBackModalOpen(true)}
                style={{ padding: '8px 16px', fontSize: 14, backgroundColor: '#d83b01', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                {preisAbweichungen.length} Preise → BC schreiben
              </button>
            )}
            {preisAbweichungen.length === 0 && !preisAbweichungenLoading && (
              <Text style={{ color: '#107c10', fontWeight: 600 }}>Alle Preise synchron</Text>
            )}
          </Stack>
          {preisAbweichungen.length > 0 && (
            <div style={{ fontSize: 12, color: '#605e5c', maxHeight: 120, overflowY: 'auto' }}>
              {preisAbweichungen.map((a, i) => (
                <div key={i}>{a.bcNummer}: lokal {Number(a.lokalerPreis).toFixed(2)} € | BC {Number(a.bcPreis).toFixed(2)} €</div>
              ))}
            </div>
          )}
        </Stack>

        {/* Auto-Archivierung */}
        <Stack
          tokens={{ childrenGap: 12 }}
          styles={{ root: { padding: '16px', backgroundColor: '#f0f9ff', borderRadius: 4, border: '1px solid #c7e0f4' } }}
          onMouseEnter={loadAutoArchivSettings}
        >
          <Text variant="medium" style={{ fontWeight: 600 }}>Automatische Archivierung nach Lieferung</Text>
          <Text style={{ color: '#605e5c' }}>
            Bestellungen werden automatisch archiviert, sobald alle Positionen den Status „Geliefert" haben
            und das Datum des letzten Wareneingangs (BCLieferdatum) mehr als die angegebene Anzahl Tage zurückliegt.
            Leer lassen = deaktiviert.
          </Text>
          <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
            <Stack.Item>
              <Label htmlFor="autoarchiv-tage">Tage nach Wareneingang</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  id="autoarchiv-tage"
                  type="number"
                  min={1}
                  step={1}
                  value={autoArchivTage}
                  onChange={e => setAutoArchivTage(e.target.value)}
                  placeholder="leer = deaktiviert"
                  style={{
                    width: 180, padding: '6px 10px', fontSize: 14,
                    border: '1px solid #8a8886', borderRadius: 4,
                    fontFamily: '"Segoe UI", sans-serif',
                  }}
                />
                <Text style={{ color: '#605e5c', fontSize: 13 }}>Tage</Text>
              </div>
            </Stack.Item>
            <Stack.Item>
              <button
                onClick={saveAutoArchivSettings}
                disabled={autoArchivSaving}
                style={{
                  padding: '7px 20px', fontSize: 14, fontWeight: 600,
                  backgroundColor: autoArchivSaving ? '#f3f2f1' : '#0078d4',
                  color: autoArchivSaving ? '#323130' : 'white',
                  border: 'none', borderRadius: 4,
                  cursor: autoArchivSaving ? 'default' : 'pointer',
                }}
              >
                {autoArchivSaving ? 'Speichere...' : 'Speichern'}
              </button>
            </Stack.Item>
          </Stack>
          {autoArchivMsg && (
            <MessageBar messageBarType={autoArchivMsg.type} onDismiss={() => setAutoArchivMsg(null)}>
              {autoArchivMsg.text}
            </MessageBar>
          )}
        </Stack>

        <Separator />

        {/* Hinweise */}
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: '16px', backgroundColor: '#f3f2f1', borderRadius: 4 } }}>
          <Text variant="medium" style={{ fontWeight: 600 }}>Hinweise</Text>
          <Text>• Die Synchronisation kann je nach Datenmenge einige Minuten dauern</Text>
          <Text>• Bestehende Artikel werden nur bei Änderungen aktualisiert</Text>
          <Text>• Einzelpreise: bidirektional – die neuere Quelle gewinnt; lokal neuer → Write-Back zu BC möglich</Text>
          <Text>• Neue Artikel und Kreditoren werden automatisch erstellt</Text>
          <Text>• Lieferstatus: wird nur aufgewertet (kein Downgrade des manuell gesetzten Status)</Text>
          <Text>• Alle Sync-Typen laufen automatisch stündlich im Hintergrund</Text>
          <Text>• Auto-Archivierung: läuft nach jedem Lieferstatus-Sync; nur Bestellungen mit vollständiger Lieferung (alle Positionen „Geliefert" + BCLieferdatum gesetzt)</Text>
        </Stack>

        {/* Sync-Historie */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="large" style={{ fontWeight: 600 }}>Sync-Protokoll</Text>
            <button
              onClick={loadSyncHistory}
              disabled={syncHistoryLoading}
              style={{
                padding: '6px 14px', fontSize: 13,
                backgroundColor: syncHistoryLoading ? '#f3f2f1' : '#0078d4',
                color: syncHistoryLoading ? '#323130' : 'white',
                border: 'none', borderRadius: 4, cursor: syncHistoryLoading ? 'default' : 'pointer'
              }}
            >
              {syncHistoryLoading ? 'Lade...' : 'Laden'}
            </button>
          </Stack>

          {syncHistory.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f2f1' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Typ</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Auslöser</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Gestartet</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Neu</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Akt.</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Übersp.</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Fehler</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #edebe9' }}>Dauer</th>
                  </tr>
                </thead>
                <tbody>
                  {syncHistory.map((h, idx) => {
                    const started = h.StartedAt ? new Date(h.StartedAt) : null;
                    const completed = h.CompletedAt ? new Date(h.CompletedAt) : null;
                    const durationSec = started && completed
                      ? ((completed.getTime() - started.getTime()) / 1000).toFixed(1) + 's'
                      : '–';
                    const statusColor = h.Status === 'success' ? '#107c10' : h.Status === 'error' ? '#a4262c' : '#797775';
                    return (
                      <tr key={h.Id} style={{ borderBottom: '1px solid #f3f2f1', backgroundColor: idx % 2 === 0 ? 'white' : '#faf9f8' }}>
                        <td style={{ padding: '6px 12px' }}>{syncTypeLabel[h.SyncType] || h.SyncType}</td>
                        <td style={{ padding: '6px 12px', color: '#605e5c' }}>{h.SyncTrigger === 'scheduled' ? 'Automatisch' : 'Manuell'}</td>
                        <td style={{ padding: '6px 12px', color: '#605e5c' }}>
                          {started ? started.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–'}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'center', color: statusColor, fontWeight: 600 }}>
                          {h.Status === 'success' ? '✓' : h.Status === 'error' ? '✗' : '⏳'}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#107c10' }}>{h.Created ?? 0}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#0078d4' }}>{h.Updated ?? 0}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#797775' }}>{h.Skipped ?? 0}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: h.Errors > 0 ? '#a4262c' : '#797775' }}>{h.Errors ?? 0}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#605e5c' }}>{durationSec}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {syncHistory.length === 0 && !syncHistoryLoading && (
            <Text style={{ color: '#605e5c', fontStyle: 'italic' }}>
              Klicke auf "Laden" um das Sync-Protokoll anzuzeigen (erfordert Migration 016).
            </Text>
          )}
        </Stack>

        {/* Sync Modals */}
        <SyncModal
          isOpen={artikelSyncModalOpen}
          onDismiss={() => setArtikelSyncModalOpen(false)}
          syncType="artikel"
          title="Artikel aus Business Central synchronisieren"
        />

        <SyncModal
          isOpen={kreditorenSyncModalOpen}
          onDismiss={() => setKreditorenSyncModalOpen(false)}
          syncType="kreditoren"
          title="Kreditoren aus Business Central synchronisieren"
        />

        <SyncModal
          isOpen={mengeneinheitenSyncModalOpen}
          onDismiss={() => setMengeneinheitenSyncModalOpen(false)}
          syncType="mengeneinheiten"
          title="Mengeneinheiten aus Business Central synchronisieren"
        />

        <SyncModal
          isOpen={lieferstatusSyncModalOpen}
          onDismiss={() => setLieferstatusSyncModalOpen(false)}
          syncType="lieferstatus"
          title="Lieferstatus aus Business Central synchronisieren"
        />

        {/* Preis Write-Back Modal (POST-basiert, eigenes SSE) */}
        <PreisWriteBackModal
          isOpen={preisWriteBackModalOpen}
          onDismiss={() => { setPreisWriteBackModalOpen(false); setPreisAbweichungen([]); }}
          updates={(Array.isArray(preisAbweichungen) ? preisAbweichungen : []).map(a => ({ bcNummer: a.bcNummer, preis: a.lokalerPreis }))}
        />
      </Stack>
    );
  };

  // ─── E-Mail-Einstellungen ────────────────────────────────────────────────

  const loadEmailSettings = async () => {
    if (emailSettingsLoaded) return;
    try {
      const response = await axios.get('/api/email-settings');
      setEmailSettings(response.data);
      setEmailSettingsLoaded(true);
    } catch (error) {
      console.error('Fehler beim Laden der E-Mail-Einstellungen:', error);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    setEmailMessage(null);
    try {
      await axios.put('/api/email-settings', emailSettings);
      setEmailMessage({ type: MessageBarType.success, text: 'E-Mail-Einstellungen gespeichert.' });
    } catch (error: any) {
      setEmailMessage({ type: MessageBarType.error, text: error.response?.data?.error || 'Fehler beim Speichern.' });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setEmailMessage(null);
    try {
      const response = await axios.post('/api/email-settings/test', {
        ...emailSettings,
        testRecipient: emailSettings.SenderEmail,
      });
      setEmailMessage({
        type: response.data.success ? MessageBarType.success : MessageBarType.error,
        text: response.data.message,
      });
    } catch (error: any) {
      setEmailMessage({ type: MessageBarType.error, text: error.response?.data?.error || 'Test fehlgeschlagen.' });
    } finally {
      setTestingEmail(false);
    }
  };

  const renderEmail = () => {
    if (!emailSettingsLoaded) loadEmailSettings();

    const presets = [
      { key: 'office365', text: 'Office 365 (smtp.office365.com:587 STARTTLS)' },
      { key: 'smarthost', text: 'Smarthost intern (rdp:25, kein TLS)' },
    ];

    const applyPreset = (key: string) => {
      if (key === 'office365') {
        setEmailSettings(s => ({ ...s, SmtpHost: 'smtp.office365.com', SmtpPort: 587, SmtpSsl: true }));
      } else if (key === 'smarthost') {
        setEmailSettings(s => ({ ...s, SmtpHost: 'rdp', SmtpPort: 25, SmtpSsl: false, SmtpUser: '', SmtpPassword: '' }));
      }
    };

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Label style={{ fontSize: 20, fontWeight: 600 }}>E-Mail / SMTP</Label>

        {emailMessage && (
          <MessageBar messageBarType={emailMessage.type} onDismiss={() => setEmailMessage(null)}>
            {emailMessage.text}
          </MessageBar>
        )}

        <Stack tokens={{ childrenGap: 4 }}>
          <Label>Voreinstellungen</Label>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            {presets.map(p => (
              <DefaultButton key={p.key} text={p.text} onClick={() => applyPreset(p.key)} />
            ))}
          </Stack>
        </Stack>

        <Separator />

        <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
          <TextField
            label="SMTP-Server"
            value={emailSettings.SmtpHost}
            onChange={(_, v) => setEmailSettings(s => ({ ...s, SmtpHost: v || '' }))}
            placeholder="z.B. smtp.office365.com"
            styles={{ root: { width: 300 } }}
            disabled={savingEmail}
          />
          <TextField
            label="Port"
            value={String(emailSettings.SmtpPort)}
            onChange={(_, v) => setEmailSettings(s => ({ ...s, SmtpPort: parseInt(v || '587') || 587 }))}
            styles={{ root: { width: 100 } }}
            disabled={savingEmail}
          />
          <Stack verticalAlign="end" style={{ paddingBottom: 4 }}>
            <Label>SSL/TLS / STARTTLS</Label>
            <input
              type="checkbox"
              checked={emailSettings.SmtpSsl}
              onChange={e => setEmailSettings(s => ({ ...s, SmtpSsl: e.target.checked }))}
              disabled={savingEmail}
              style={{ width: 18, height: 18, marginTop: 6 }}
            />
          </Stack>
        </Stack>

        <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
          <TextField
            label="Benutzername"
            value={emailSettings.SmtpUser}
            onChange={(_, v) => setEmailSettings(s => ({ ...s, SmtpUser: v || '' }))}
            placeholder="Leer lassen für anonymen Relay"
            styles={{ root: { width: 300 } }}
            disabled={savingEmail}
          />
          <TextField
            label="Passwort"
            type="password"
            canRevealPassword
            value={emailSettings.SmtpPassword}
            onChange={(_, v) => setEmailSettings(s => ({ ...s, SmtpPassword: v || '' }))}
            styles={{ root: { width: 300 } }}
            disabled={savingEmail}
          />
        </Stack>

        <Separator />

        <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
          <TextField
            label="Absender-E-Mail"
            value={emailSettings.SenderEmail}
            onChange={(_, v) => setEmailSettings(s => ({ ...s, SenderEmail: v || '' }))}
            styles={{ root: { width: 300 } }}
            disabled={savingEmail}
          />
          <TextField
            label="Absender-Name"
            value={emailSettings.SenderName}
            onChange={(_, v) => setEmailSettings(s => ({ ...s, SenderName: v || '' }))}
            styles={{ root: { width: 300 } }}
            disabled={savingEmail}
          />
        </Stack>

        <TextField
          label="Einkauf-Benachrichtigungs-E-Mail"
          description="Empfänger wenn der Einkauf benachrichtigt werden soll (z.B. Verteilerliste einkauf@boegra.com)"
          value={emailSettings.EinkaufEmail}
          onChange={(_, v) => setEmailSettings(s => ({ ...s, EinkaufEmail: v || '' }))}
          placeholder="einkauf@boegra.com"
          styles={{ root: { maxWidth: 400 } }}
          disabled={savingEmail}
        />

        <Stack horizontal tokens={{ childrenGap: 12 }}>
          <PrimaryButton
            text="Speichern"
            onClick={handleSaveEmailSettings}
            disabled={savingEmail || testingEmail}
          />
          <DefaultButton
            text={testingEmail ? 'Sende Test-Mail…' : 'Test-Mail senden'}
            onClick={handleTestEmail}
            disabled={savingEmail || testingEmail || !emailSettings.SmtpHost}
            iconProps={{ iconName: 'Mail' }}
          />
        </Stack>
      </Stack>
    );
  };

  // ─── Benachrichtigungssteuerung ─────────────────────────────────────────────

  const loadNotifPrefs = async () => {
    if (notifPrefsLoaded) return;
    try {
      const resp = await axios.get('/api/notification-preferences');
      setNotifPrefs(resp.data);
      setNotifPrefsLoaded(true);
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungseinstellungen:', error);
    }
  };

  const handleSaveNotifPrefs = async () => {
    setSavingNotifPrefs(true);
    setNotifMessage(null);
    try {
      await axios.put('/api/notification-preferences', notifPrefs);
      setNotifMessage({ type: MessageBarType.success, text: 'Benachrichtigungseinstellungen gespeichert.' });
    } catch (error: any) {
      setNotifMessage({ type: MessageBarType.error, text: error.response?.data?.error || 'Fehler beim Speichern.' });
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  // ─── E-Mail-Presets ─────────────────────────────────────────────────────────

  const EVENT_TYPE_OPTIONS = [
    { key: '', text: 'Kein automatischer Versand (manuell)' },
    { key: 'freigabe_anforderung', text: 'Freigabe angefordert (Flow zugewiesen)' },
    { key: 'neue_bestellung', text: 'Neue Bestellung erstellt' },
    { key: 'position_freigegeben', text: 'Position freigegeben' },
    { key: 'position_abgelehnt', text: 'Position abgelehnt' },
    { key: 'flow_komplett', text: 'Alle Flows vollständig freigegeben' },
    { key: 'kommentar', text: 'Neuer Kommentar' },
    { key: 'mention', text: '@-Erwähnung' },
    { key: 'position_geaendert', text: 'Position geändert' },
  ];

  const VARIABLES = [
    { group: 'Empfänger', items: [
      { name: '{{Empfänger.Name}}', desc: 'Vollständiger Name' },
      { name: '{{Empfänger.Vorname}}', desc: 'Vorname' },
      { name: '{{Empfänger.Nachname}}', desc: 'Nachname' },
      { name: '{{Empfänger.Email}}', desc: 'E-Mail-Adresse' },
    ]},
    { group: 'Bestellung', items: [
      { name: '{{Bestellung.Titel}}', desc: 'Titel der Bestellung' },
      { name: '{{Bestellung.ID}}', desc: 'Interne Bestellnummer' },
      { name: '{{Bestellung.Antragsteller}}', desc: 'Name des Antragstellers' },
      { name: '{{Bestellung.Ersteller}}', desc: 'Name des Erstellers' },
      { name: '{{Bestellung.Status}}', desc: 'Aktueller Bestellstatus' },
      { name: '{{Bestellung.Link}}', desc: 'Link zur Bestellung (URL)' },
    ]},
    { group: 'Position', items: [
      { name: '{{Position.Nummer}}', desc: 'Positions-Nummer (erste Position bei Digest)' },
      { name: '{{Position.Artikel}}', desc: 'Artikelname (erste Position bei Digest)' },
      { name: '{{Position.Status}}', desc: 'Genehmigungsstatus (erste Position bei Digest)' },
    ]},
    { group: 'Änderungen (Digest)', items: [
      { name: '{{Änderungen.Liste}}', desc: 'HTML-Tabelle aller geänderten Positionen (für Digest-Mails)' },
      { name: '{{Änderungen.Anzahl}}', desc: 'Anzahl geänderter Positionen' },
      { name: '{{Änderung.Durch}}', desc: 'Name der Person, die die Änderung vorgenommen hat' },
    ]},
    { group: 'Freigaben (Digest)', items: [
      { name: '{{Freigaben.Liste}}', desc: 'HTML-Tabelle aller Freigabe-Entscheidungen (für freigabe_entscheidung-Digest)' },
      { name: '{{Freigaben.Anzahl}}', desc: 'Anzahl entschiedener Positionen' },
      { name: '{{Freigabe.Durch}}', desc: 'Name des Genehmigers (erste Entscheidung)' },
      { name: '{{Freigabe.Status}}', desc: 'Freigegeben oder Abgelehnt (erste Entscheidung)' },
      { name: '{{Freigabe.Abteilung}}', desc: 'Abteilung der Freigabe (erste Entscheidung)' },
    ]},
    { group: 'Freigabe-Anforderungen (Digest)', items: [
      { name: '{{Anforderungen.Liste}}', desc: 'HTML-Tabelle der Positionen, für die eine Freigabe angefordert wird (für freigabe_anforderung-Digest)' },
      { name: '{{Anforderungen.Anzahl}}', desc: 'Anzahl der Positionen mit Freigabe-Anforderung' },
      { name: '{{Anforderung.Abteilung}}', desc: 'Abteilung der ersten Anforderung' },
    ]},
    { group: 'Abteilung', items: [
      { name: '{{Abteilung.Name}}', desc: 'Abteilungsname (bei Freigabe)' },
    ]},
    { group: 'Kommentar', items: [
      { name: '{{Kommentar.Text}}', desc: 'Kommentartext' },
      { name: '{{Kommentar.Autor}}', desc: 'Name des Kommentarautors' },
    ]},
    { group: 'System', items: [
      { name: '{{System.Datum}}', desc: 'Aktuelles Datum (TT.MM.JJJJ)' },
      { name: '{{System.App}}', desc: 'App-Name (Bögra-Bestellwesen)' },
    ]},
  ];

  const DUMMY_VARS: Record<string, string> = {
    '{{Empfänger.Name}}': 'Max Mustermann', '{{Empfänger.Vorname}}': 'Max',
    '{{Empfänger.Nachname}}': 'Mustermann', '{{Empfänger.Email}}': 'max.mustermann@boegra.com',
    '{{Bestellung.Titel}}': 'Büromaterial Q3 2026', '{{Bestellung.ID}}': '42',
    '{{Bestellung.Antragsteller}}': 'Anna Schmidt', '{{Bestellung.Ersteller}}': 'Anna Schmidt',
    '{{Bestellung.Status}}': 'Eingereicht', '{{Bestellung.Link}}': 'http://bestellwesen.webapps.boegra.com/bestellungen/42',
    '{{Position.Nummer}}': '3', '{{Position.Artikel}}': 'Drucker-Toner schwarz', '{{Position.Status}}': 'Offen',
    '{{Abteilung.Name}}': 'IT',
    '{{Kommentar.Text}}': 'Bitte schnellstmöglich bestellen.', '{{Kommentar.Autor}}': 'Sven Schumacher',
    '{{Änderung.Durch}}': 'Anna Schmidt',
    '{{Änderungen.Anzahl}}': '3',
    '{{Änderungen.Liste}}': `<table style="border-collapse:collapse;width:100%;font-size:13px;">
      <thead><tr style="background:#f0f0f0;">
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Pos.</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Artikel</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Geändert durch</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Zeitpunkt</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">1</td><td style="padding:4px 8px;border:1px solid #ddd;">Drucker-Toner schwarz</td><td style="padding:4px 8px;border:1px solid #ddd;">Anna Schmidt</td><td style="padding:4px 8px;border:1px solid #ddd;">14.06.2026, 10:15</td></tr>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">2</td><td style="padding:4px 8px;border:1px solid #ddd;">Kopierpapier A4</td><td style="padding:4px 8px;border:1px solid #ddd;">Anna Schmidt</td><td style="padding:4px 8px;border:1px solid #ddd;">14.06.2026, 10:22</td></tr>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">3</td><td style="padding:4px 8px;border:1px solid #ddd;">Heftklammern</td><td style="padding:4px 8px;border:1px solid #ddd;">Anna Schmidt</td><td style="padding:4px 8px;border:1px solid #ddd;">14.06.2026, 10:30</td></tr>
      </tbody></table>`,
    '{{Freigaben.Anzahl}}': '2',
    '{{Freigaben.Liste}}': `<table style="border-collapse:collapse;width:100%;font-size:13px;">
      <thead><tr style="background:#f0f0f0;">
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Pos.</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Artikel</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Abteilung</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Genehmiger</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Entscheidung</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Zeitpunkt</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">1</td><td style="padding:4px 8px;border:1px solid #ddd;">Drucker-Toner schwarz</td><td style="padding:4px 8px;border:1px solid #ddd;">EDV</td><td style="padding:4px 8px;border:1px solid #ddd;">Sven Schumacher</td><td style="padding:4px 8px;border:1px solid #ddd;color:#107c10;font-weight:600">Freigegeben</td><td style="padding:4px 8px;border:1px solid #ddd;">15.06.2026, 09:30</td></tr>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">2</td><td style="padding:4px 8px;border:1px solid #ddd;">Kopierpapier A4</td><td style="padding:4px 8px;border:1px solid #ddd;">Geschäftsführung</td><td style="padding:4px 8px;border:1px solid #ddd;">Linser, Tobias</td><td style="padding:4px 8px;border:1px solid #ddd;color:#107c10;font-weight:600">Freigegeben</td><td style="padding:4px 8px;border:1px solid #ddd;">15.06.2026, 11:05</td></tr>
      </tbody></table>`,
    '{{Freigabe.Durch}}': 'Sven Schumacher',
    '{{Freigabe.Status}}': 'Freigegeben',
    '{{Freigabe.Abteilung}}': 'EDV',
    '{{Anforderungen.Anzahl}}': '2',
    '{{Anforderungen.Liste}}': `<table style="border-collapse:collapse;width:100%;font-size:13px;">
      <thead><tr style="background:#f0f0f0;">
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Pos.</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Artikel</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Abteilung</th>
        <th style="padding:4px 8px;border:1px solid #ddd;text-align:left;">Zeitpunkt</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">1</td><td style="padding:4px 8px;border:1px solid #ddd;">Drucker-Toner schwarz</td><td style="padding:4px 8px;border:1px solid #ddd;">EDV</td><td style="padding:4px 8px;border:1px solid #ddd;">15.06.2026, 08:45</td></tr>
        <tr><td style="padding:4px 8px;border:1px solid #ddd;">2</td><td style="padding:4px 8px;border:1px solid #ddd;">Kopierpapier A4</td><td style="padding:4px 8px;border:1px solid #ddd;">EDV</td><td style="padding:4px 8px;border:1px solid #ddd;">15.06.2026, 08:45</td></tr>
      </tbody></table>`,
    '{{Anforderung.Abteilung}}': 'EDV',
    '{{System.Datum}}': new Date().toLocaleDateString('de-DE'), '{{System.App}}': 'Bögra-Bestellwesen',
  };

  const loadEmailTemplates = async () => {
    if (emailTemplatesLoaded) return;
    try {
      const response = await axios.get('/api/email-templates');
      setEmailTemplates(response.data);
      setEmailTemplatesLoaded(true);
    } catch (error) {
      console.error('Fehler beim Laden der E-Mail-Vorlagen:', error);
    }
  };

  const insertVariable = (variable: string) => {
    const ta = bodyTextareaRef.current;
    if (!ta || !editingTemplate) return;
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const newBody = editingTemplate.Body.substring(0, start) + variable + editingTemplate.Body.substring(end);
    setEditingTemplate(t => t ? { ...t, Body: newBody } : null);
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = start + variable.length;
      ta.focus();
    }, 0);
  };

  const renderPreviewHtml = (body: string) => {
    let html = body;
    Object.entries(DUMMY_VARS).forEach(([k, v]) => { html = html.split(k).join(v); });
    return html;
  };

  const handleSelectTemplate = (t: any) => {
    setSelectedTemplateId(t.Id);
    setEditingTemplate({ ...t });
    setTemplateMessage(null);
    setPreviewOpen(false);
  };

  const handleNewTemplate = () => {
    const newT = { Id: -1, PresetName: 'Neues Preset', EventType: null, Subject: '', Body: '', IsActive: true };
    setSelectedTemplateId(-1);
    setEditingTemplate(newT);
    setTemplateMessage(null);
    setPreviewOpen(false);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    setTemplateMessage(null);
    try {
      if (editingTemplate.Id === -1) {
        const resp = await axios.post('/api/email-templates', editingTemplate);
        setEmailTemplates(ts => [...ts, resp.data]);
        setSelectedTemplateId(resp.data.Id);
        setEditingTemplate(resp.data);
      } else {
        const resp = await axios.put(`/api/email-templates/${editingTemplate.Id}`, editingTemplate);
        setEmailTemplates(ts => ts.map(t => t.Id === resp.data.Id ? resp.data : t));
        setEditingTemplate(resp.data);
      }
      setTemplateMessage({ type: MessageBarType.success, text: 'Vorlage gespeichert.' });
    } catch (error: any) {
      setTemplateMessage({ type: MessageBarType.error, text: error.response?.data?.error || 'Fehler beim Speichern.' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!editingTemplate || editingTemplate.Id === -1) return;
    if (!window.confirm(`Vorlage „${editingTemplate.PresetName}" wirklich löschen?`)) return;
    try {
      await axios.delete(`/api/email-templates/${editingTemplate.Id}`);
      setEmailTemplates(ts => ts.filter(t => t.Id !== editingTemplate.Id));
      setEditingTemplate(null);
      setSelectedTemplateId(null);
    } catch (error: any) {
      setTemplateMessage({ type: MessageBarType.error, text: error.response?.data?.error || 'Fehler beim Löschen.' });
    }
  };

  const loadDokTypen = async () => {
    try {
      const typen = await dokumentTypenApi.getAll();
      setDokTypen(typen);
      setDokTypenLoaded(true);
    } catch {
      setDokTypMsg({ type: MessageBarType.error, text: 'Dokumenttypen konnten nicht geladen werden.' });
    }
  };

  const handleDokTypCreate = async () => {
    if (!dokTypNeuName.trim()) return;
    setDokTypSaving(true);
    try {
      await dokumentTypenApi.create(dokTypNeuName.trim(), dokTypNeuFarbe, dokTypNeuReihe);
      setDokTypNeuName('');
      setDokTypNeuFarbe('#888888');
      setDokTypNeuReihe(100);
      setDokTypMsg({ type: MessageBarType.success, text: 'Dokumenttyp angelegt.' });
      await loadDokTypen();
    } catch (err: any) {
      setDokTypMsg({ type: MessageBarType.error, text: err?.response?.data?.error || 'Fehler.' });
    } finally {
      setDokTypSaving(false);
    }
  };

  const handleDokTypSaveEdit = async () => {
    if (!editingDokTyp) return;
    setDokTypSaving(true);
    try {
      await dokumentTypenApi.update(editingDokTyp.Id, {
        name: editingDokTyp.Name,
        farbe: editingDokTyp.Farbe,
        reihenfolge: editingDokTyp.Reihenfolge,
      });
      setEditingDokTyp(null);
      setDokTypMsg({ type: MessageBarType.success, text: 'Gespeichert.' });
      await loadDokTypen();
    } catch (err: any) {
      setDokTypMsg({ type: MessageBarType.error, text: err?.response?.data?.error || 'Fehler.' });
    } finally {
      setDokTypSaving(false);
    }
  };

  const handleDokTypDelete = async (id: number) => {
    if (!window.confirm('Dokumenttyp wirklich löschen?')) return;
    try {
      await dokumentTypenApi.delete(id);
      setDokTypMsg({ type: MessageBarType.success, text: 'Gelöscht.' });
      await loadDokTypen();
    } catch (err: any) {
      setDokTypMsg({ type: MessageBarType.error, text: err?.response?.data?.error || 'System-Typen können nicht gelöscht werden.' });
    }
  };

  const renderDokumentTypen = () => {
    if (!dokTypenLoaded) loadDokTypen();
    return (
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: '16px 0' } }}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Dokumenttypen (DMS)</Text>
        <Text>Definiere die verfügbaren Tags für Dokumente in Bestellungen. System-Typen (blau markiert) können nicht gelöscht werden.</Text>

        {dokTypMsg && (
          <MessageBar
            messageBarType={dokTypMsg.type}
            onDismiss={() => setDokTypMsg(null)}
          >
            {dokTypMsg.text}
          </MessageBar>
        )}

        {/* Liste */}
        <Stack tokens={{ childrenGap: 0 }}>
          {dokTypen.map(typ => (
            <Stack
              key={typ.Id}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{ root: { borderBottom: '1px solid #eee', padding: '6px 4px' } }}
            >
              {editingDokTyp?.Id === typ.Id ? (
                <>
                  <Stack.Item>
                    <input
                      type="color"
                      value={editingDokTyp.Farbe}
                      onChange={e => setEditingDokTyp({ ...editingDokTyp, Farbe: e.target.value })}
                      style={{ width: 32, height: 28, border: 'none', cursor: 'pointer', padding: 0 }}
                    />
                  </Stack.Item>
                  <Stack.Item grow>
                    <TextField
                      value={editingDokTyp.Name}
                      onChange={(_, v) => setEditingDokTyp({ ...editingDokTyp, Name: v || '' })}
                    />
                  </Stack.Item>
                  <Stack.Item styles={{ root: { width: 80 } }}>
                    <TextField
                      label=""
                      type="number"
                      value={String(editingDokTyp.Reihenfolge)}
                      onChange={(_, v) => setEditingDokTyp({ ...editingDokTyp, Reihenfolge: Number(v) || 100 })}
                      placeholder="Reihenfolge"
                    />
                  </Stack.Item>
                  <DefaultButton text="Speichern" onClick={handleDokTypSaveEdit} disabled={dokTypSaving} />
                  <DefaultButton text="Abbrechen" onClick={() => setEditingDokTyp(null)} />
                </>
              ) : (
                <>
                  <span
                    style={{
                      background: typ.Farbe,
                      color: '#fff',
                      borderRadius: 3,
                      padding: '2px 10px',
                      fontSize: 13,
                      fontWeight: 600,
                      minWidth: 90,
                      textAlign: 'center',
                      display: 'inline-block',
                    }}
                  >
                    {typ.Name}
                  </span>
                  <Text variant="small" styles={{ root: { color: '#666', minWidth: 60 } }}>
                    Reihenfolge: {typ.Reihenfolge}
                  </Text>
                  {typ.IstSystem ? (
                    <Text variant="small" styles={{ root: { color: '#0078d4', fontStyle: 'italic' } }}>
                      System
                    </Text>
                  ) : (
                    <Text variant="small" styles={{ root: { color: '#666' } }}>Angepasst</Text>
                  )}
                  <Stack.Item grow />
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    title="Bearbeiten"
                    onClick={() => setEditingDokTyp({ ...typ })}
                  />
                  {!typ.IstSystem && (
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      title="Löschen"
                      styles={{ root: { color: '#a4262c' } }}
                      onClick={() => handleDokTypDelete(typ.Id)}
                    />
                  )}
                </>
              )}
            </Stack>
          ))}
        </Stack>

        {/* Neuen Typ anlegen */}
        <Separator />
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Neuen Dokumenttyp anlegen</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end" wrap>
          <Stack.Item>
            <Label>Farbe</Label>
            <input
              type="color"
              value={dokTypNeuFarbe}
              onChange={e => setDokTypNeuFarbe(e.target.value)}
              style={{ width: 40, height: 32, border: 'none', cursor: 'pointer', padding: 0 }}
            />
          </Stack.Item>
          <Stack.Item styles={{ root: { minWidth: 180 } }}>
            <TextField
              label="Name"
              value={dokTypNeuName}
              onChange={(_, v) => setDokTypNeuName(v || '')}
              placeholder="z. B. Bestellformular"
            />
          </Stack.Item>
          <Stack.Item styles={{ root: { width: 110 } }}>
            <TextField
              label="Reihenfolge"
              type="number"
              value={String(dokTypNeuReihe)}
              onChange={(_, v) => setDokTypNeuReihe(Number(v) || 100)}
            />
          </Stack.Item>
          <Stack.Item>
            <PrimaryButton
              text="Anlegen"
              iconProps={{ iconName: 'Add' }}
              onClick={handleDokTypCreate}
              disabled={dokTypSaving || !dokTypNeuName.trim()}
            />
          </Stack.Item>
        </Stack>
      </Stack>
    );
  };

  const renderEmailPresets = () => {
    if (!emailTemplatesLoaded) loadEmailTemplates();

    const eventLabel = (key: string | null) =>
      EVENT_TYPE_OPTIONS.find(o => o.key === (key || ''))?.text || 'Kein automatischer Versand';

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Label style={{ fontSize: 20, fontWeight: 600 }}>E-Mail - Presets</Label>

        {/* ── Zuordnungsmatrix ── */}
        <Stack tokens={{ childrenGap: 8 }}>
          <Label style={{ fontWeight: 600 }}>Zuordnungsmatrix</Label>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ background: '#0078d4', color: 'white', padding: '6px 10px', textAlign: 'left' }}>Ereignis</th>
                <th style={{ background: '#0078d4', color: 'white', padding: '6px 10px', textAlign: 'left' }}>Zugeordnetes Preset</th>
              </tr>
            </thead>
            <tbody>
              {EVENT_TYPE_OPTIONS.filter(o => o.key !== '').map((evt, i) => {
                const assigned = emailTemplates.find(t => t.EventType === evt.key && t.IsActive);
                return (
                  <tr key={evt.key} style={{ background: i % 2 === 1 ? '#f3f2f1' : 'white' }}>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #edebe9' }}>{evt.text}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #edebe9' }}>
                      {assigned
                        ? <span
                            style={{ color: '#0078d4', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => handleSelectTemplate(assigned)}
                          >{assigned.PresetName}</span>
                        : <span style={{ color: '#a19f9d' }}>–</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Stack>

        <Separator />

        <Stack horizontal tokens={{ childrenGap: 16 }} style={{ alignItems: 'flex-start' }}>
          {/* ── Preset-Liste ── */}
          <Stack style={{ width: 220, flexShrink: 0 }} tokens={{ childrenGap: 8 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Label style={{ fontWeight: 600 }}>Presets</Label>
              <DefaultButton iconProps={{ iconName: 'Add' }} text="Neu" onClick={handleNewTemplate} styles={{ root: { minWidth: 60 } }} />
            </Stack>
            <Stack tokens={{ childrenGap: 4 }}>
              {emailTemplates.map(t => (
                <div
                  key={t.Id}
                  onClick={() => handleSelectTemplate(t)}
                  style={{
                    padding: '8px 10px', cursor: 'pointer', borderRadius: 4,
                    background: selectedTemplateId === t.Id ? '#deecf9' : 'transparent',
                    border: selectedTemplateId === t.Id ? '1px solid #0078d4' : '1px solid transparent',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.PresetName}</div>
                  <div style={{ fontSize: 11, color: '#605e5c' }}>{eventLabel(t.EventType)}</div>
                </div>
              ))}
              {emailTemplates.length === 0 && emailTemplatesLoaded && (
                <div style={{ color: '#a19f9d', fontSize: 13, padding: '8px 0' }}>Keine Presets vorhanden</div>
              )}
            </Stack>
          </Stack>

          {/* ── Editor ── */}
          {editingTemplate && (
            <Stack style={{ flex: 1 }} tokens={{ childrenGap: 12 }}>
              {templateMessage && (
                <MessageBar messageBarType={templateMessage.type} onDismiss={() => setTemplateMessage(null)}>
                  {templateMessage.text}
                </MessageBar>
              )}

              <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                <TextField
                  label="Preset-Name"
                  value={editingTemplate.PresetName}
                  onChange={(_, v) => setEditingTemplate(t => t ? { ...t, PresetName: v || '' } : null)}
                  styles={{ root: { width: 260 } }}
                  disabled={savingTemplate}
                />
                <Dropdown
                  label="Auslöser (Ereignis)"
                  selectedKey={editingTemplate.EventType || ''}
                  options={EVENT_TYPE_OPTIONS}
                  onChange={(_, o) => setEditingTemplate(t => t ? { ...t, EventType: (o?.key as string) || null } : null)}
                  styles={{ root: { width: 300 } }}
                  disabled={savingTemplate}
                />
              </Stack>

              <TextField
                label="Betreff"
                value={editingTemplate.Subject}
                onChange={(_, v) => setEditingTemplate(t => t ? { ...t, Subject: v || '' } : null)}
                disabled={savingTemplate}
                placeholder="z.B. Neue Bestellung: {{Bestellung.Titel}}"
              />

              <Stack horizontal tokens={{ childrenGap: 16 }} style={{ alignItems: 'flex-start' }}>
                {/* Body-Editor */}
                <Stack style={{ flex: 1 }} tokens={{ childrenGap: 4 }}>
                  <Label>Mail-Body (HTML)</Label>
                  <textarea
                    ref={bodyTextareaRef}
                    value={editingTemplate.Body}
                    onChange={e => setEditingTemplate(t => t ? { ...t, Body: e.target.value } : null)}
                    disabled={savingTemplate}
                    rows={18}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      fontFamily: '"Cascadia Code", "Consolas", monospace', fontSize: 12,
                      border: '1px solid #a19f9d', borderRadius: 2, padding: '8px 10px',
                      resize: 'vertical', outline: 'none',
                    }}
                    onFocus={e => (e.target.style.borderColor = '#0078d4')}
                    onBlur={e => (e.target.style.borderColor = '#a19f9d')}
                  />
                </Stack>

                {/* Variablen-Referenz */}
                <Stack style={{ width: 220, flexShrink: 0, background: '#f3f2f1', borderRadius: 4, padding: '10px 12px' }} tokens={{ childrenGap: 10 }}>
                  <Label style={{ fontWeight: 600, fontSize: 13 }}>Variablen einfügen</Label>
                  <div style={{ fontSize: 11, color: '#605e5c', marginTop: -6 }}>Klick = an Cursorposition einfügen</div>
                  {VARIABLES.map(group => (
                    <Stack key={group.group} tokens={{ childrenGap: 3 }}>
                      <Label style={{ fontSize: 11, fontWeight: 700, color: '#0078d4', margin: 0 }}>{group.group}</Label>
                      {group.items.map(item => (
                        <div
                          key={item.name}
                          title={item.desc}
                          onClick={() => insertVariable(item.name)}
                          style={{
                            fontSize: 11, cursor: 'pointer', padding: '2px 4px',
                            borderRadius: 3, color: '#323130',
                            fontFamily: 'monospace',
                            background: 'white', border: '1px solid #edebe9',
                          }}
                        >
                          {item.name}
                          <div style={{ fontSize: 10, color: '#a19f9d', fontFamily: 'sans-serif' }}>{item.desc}</div>
                        </div>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 8 }}>
                <PrimaryButton text="Speichern" onClick={handleSaveTemplate} disabled={savingTemplate} />
                <DefaultButton
                  text={previewOpen ? 'Vorschau schließen' : 'Vorschau'}
                  iconProps={{ iconName: 'Preview' }}
                  onClick={() => setPreviewOpen(p => !p)}
                />
                {editingTemplate.Id !== -1 && (
                  <DefaultButton
                    text="Löschen"
                    iconProps={{ iconName: 'Delete' }}
                    onClick={handleDeleteTemplate}
                    disabled={savingTemplate}
                    styles={{ root: { marginLeft: 'auto' }, label: { color: '#a4262c' } }}
                  />
                )}
              </Stack>

              {/* Vorschau */}
              {previewOpen && (
                <Stack tokens={{ childrenGap: 6 }}>
                  <Label style={{ fontWeight: 600 }}>Vorschau (Dummy-Daten)</Label>
                  <div style={{ background: '#f9f9f9', border: '1px solid #edebe9', borderRadius: 4, padding: 16, fontSize: 13 }}>
                    <div style={{ marginBottom: 8, color: '#605e5c', fontSize: 12 }}>
                      <strong>Betreff:</strong> {renderPreviewHtml(editingTemplate.Subject)}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: renderPreviewHtml(editingTemplate.Body) }} />
                  </div>
                </Stack>
              )}
            </Stack>
          )}

          {!editingTemplate && (
            <div style={{ flex: 1, color: '#a19f9d', fontSize: 13, paddingTop: 8 }}>
              Preset aus der Liste wählen oder „Neu" klicken.
            </div>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderProfil = () => {
    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Label style={{ fontSize: 20, fontWeight: 600 }}>Benutzerprofil</Label>

        {message && (
          <MessageBar
            messageBarType={message.type}
            onDismiss={() => setMessage(null)}
          >
            {message.text}
          </MessageBar>
        )}

        {/* Benutzer-Informationen (Read-Only) */}
        <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '16px', backgroundColor: '#f3f2f1', borderRadius: 4 } }}>
          <Text variant="large" style={{ fontWeight: 600, marginBottom: 8 }}>Benutzer-Informationen</Text>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Text style={{ fontWeight: 600, minWidth: 120 }}>Name:</Text>
            <Text>{user?.displayName || 'Nicht verfügbar'}</Text>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Text style={{ fontWeight: 600, minWidth: 120 }}>E-Mail:</Text>
            <Text>{user?.email || 'Nicht verfügbar'}</Text>
          </Stack>
        </Stack>

        {/* Rollen */}
        <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '16px', backgroundColor: '#f3f2f1', borderRadius: 4 } }}>
          <Text variant="large" style={{ fontWeight: 600, marginBottom: 8 }}>Rollen</Text>
          {permissions?.roles && permissions.roles.length > 0 ? (
            <Stack tokens={{ childrenGap: 6 }}>
              {permissions.roles.map((role, index) => (
                <Text
                  key={index}
                  style={{
                    fontWeight: index === 0 ? 600 : 400,
                    fontSize: index === 0 ? 16 : 14
                  }}
                >
                  • {role} {index === 0 && '(Primär)'}
                </Text>
              ))}
            </Stack>
          ) : (
            <Text>Keine Rollen zugewiesen</Text>
          )}
        </Stack>

        <Separator />

        {/* Bestellungs-Standardwerte (Editierbar) */}
        <Label style={{ fontSize: 20, fontWeight: 600 }}>Bestellungs-Standardwerte</Label>
        <Text>
          Diese Werte werden automatisch beim Anlegen einer neuen Bestellung eingetragen und können jederzeit geändert werden.
        </Text>

        {loadingProfileSettings ? (
          <Spinner label="Lade Profil-Einstellungen..." />
        ) : (
          <Stack tokens={{ childrenGap: 12 }}>
            <TextField
              label="Personalnummer"
              placeholder="z.B. 1234"
              value={profileSettings.personalnummer || ''}
              onChange={(_, value) => setProfileSettings({ ...profileSettings, personalnummer: value })}
              disabled={savingProfileSettings}
            />
            <TextField
              label="Kostenstelle"
              placeholder="z.B. 0815"
              value={profileSettings.anforderndeKostenstelle || ''}
              onChange={(_, value) => setProfileSettings({ ...profileSettings, anforderndeKostenstelle: value })}
              disabled={savingProfileSettings}
            />
            <TextField
              label="Durchwahl"
              placeholder="z.B. 123"
              value={profileSettings.durchwahl || ''}
              onChange={(_, value) => setProfileSettings({ ...profileSettings, durchwahl: value })}
              disabled={savingProfileSettings}
            />

            <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 10 } }}>
              <PrimaryButton
                text="Speichern"
                onClick={handleSaveProfileSettings}
                disabled={savingProfileSettings}
              />
              <DefaultButton
                text="Zurücksetzen"
                onClick={() => loadProfileSettings()}
                disabled={savingProfileSettings}
              />
            </Stack>
          </Stack>
        )}

        <Separator />

        {/* E-Mail-Benachrichtigungen */}
        <Label style={{ fontSize: 20, fontWeight: 600 }}>E-Mail-Benachrichtigungen</Label>
        <Text>Lege fest, bei welchen Ereignissen du eine E-Mail-Benachrichtigung erhalten möchtest. Standardmäßig sind alle Benachrichtigungen aktiv.</Text>

        {(() => { if (!notifPrefsLoaded) loadNotifPrefs(); return null; })()}

        {notifMessage && (
          <MessageBar messageBarType={notifMessage.type} onDismiss={() => setNotifMessage(null)}>
            {notifMessage.text}
          </MessageBar>
        )}

        <Stack tokens={{ childrenGap: 10 }}>
          {NOTIFICATION_EVENTS.map(evt => (
            <Checkbox
              key={evt.key}
              label={evt.label}
              checked={notifPrefs[evt.key] !== false}
              onChange={(_, checked) => setNotifPrefs(p => ({ ...p, [evt.key]: !!checked }))}
              disabled={savingNotifPrefs}
              onRenderLabel={() => (
                <Stack style={{ paddingLeft: 4 }}>
                  <Text style={{ fontWeight: 600, fontSize: 14 }}>{evt.label}</Text>
                  <Text style={{ fontSize: 12, color: '#605e5c' }}>{evt.desc}</Text>
                </Stack>
              )}
            />
          ))}
        </Stack>

        <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 6 } }}>
          <PrimaryButton
            text="Benachrichtigungen speichern"
            onClick={handleSaveNotifPrefs}
            disabled={savingNotifPrefs || !notifPrefsLoaded}
          />
          <DefaultButton
            text="Alle aktivieren"
            onClick={() => setNotifPrefs(Object.fromEntries(NOTIFICATION_EVENTS.map(e => [e.key, true])))}
            disabled={savingNotifPrefs}
          />
          <DefaultButton
            text="Alle deaktivieren"
            onClick={() => setNotifPrefs(Object.fromEntries(NOTIFICATION_EVENTS.map(e => [e.key, false])))}
            disabled={savingNotifPrefs}
          />
        </Stack>
      </Stack>
    );
  };

  if (isLoadingPermissions) {
    return (
      <Stack
        verticalAlign="center"
        horizontalAlign="center"
        styles={{ root: { height: '100%', padding: 40 } }}
      >
        <Spinner size={SpinnerSize.large} label="Lade Berechtigungen..." />
      </Stack>
    );
  }

  return (
    <Stack styles={{ root: { padding: '20px 32px', maxWidth: 1400 } }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: { marginBottom: 24 } }}>
        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
          Einstellungen
        </Text>
      </Stack>

      <Pivot
        selectedKey={selectedTab}
        onLinkClick={(item) => item && setSelectedTab(item.props.itemKey!)}
        styles={{
          root: {
            marginBottom: 20
          }
        }}
      >
        <PivotItem
          headerText="Profil"
          itemKey="profil"
        >
          {renderProfil()}
        </PivotItem>

        {(permissions?.isAdmin || permissions?.isEinkauf) && (
          <PivotItem
            headerText="Berechtigungen"
            itemKey="berechtigungen"
          >
            {renderBerechtigungen()}
          </PivotItem>
        )}

        {(permissions?.isAdmin || permissions?.isEinkauf) && (
          <PivotItem
            headerText="Freigabe-Matrix"
            itemKey="freigabe"
          >
            {renderFreigabe()}
          </PivotItem>
        )}

        {(permissions?.isAdmin || permissions?.isEinkauf) && (
          <PivotItem
            headerText="Sync"
            itemKey="sync"
          >
            {renderSync()}
          </PivotItem>
        )}

        {permissions?.isAdmin && (
          <PivotItem
            headerText="E-Mail"
            itemKey="email"
          >
            {renderEmail()}
          </PivotItem>
        )}

        {permissions?.isAdmin && (
          <PivotItem
            headerText="E-Mail - Presets"
            itemKey="email-presets"
          >
            {renderEmailPresets()}
          </PivotItem>
        )}

        {(permissions?.isAdmin || permissions?.isEinkauf) && (
          <PivotItem
            headerText="Dokumenttypen"
            itemKey="dokument-typen"
          >
            {renderDokumentTypen()}
          </PivotItem>
        )}
      </Pivot>
    </Stack>
  );
};
